import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useT } from '@/lib/i18n';
import { Film, ArrowRight } from 'lucide-react';
import ReferralCard from '@/components/ReferralCard';
import { SkeletonVideoRow, SkeletonCard } from '@/components/premium/Skeleton';
import {
  FeaturedClip, CalendarWidget, ActivityTimeline, PlanCard, EmptyOnboarding,
} from './dashboard/sections';
import CreatorProfileCard from './dashboard/CreatorProfileCard';
import CreditsCard from '@/components/CreditsCard';
import {
  HeroV2, QuickActionsV2, TodayCard, AIInsightCard, useDashboardExtras,
} from './dashboard/PremiumSections';

const Dashboard = () => {
  const { user } = useAuth();
  const { t } = useT();
  const [analytics, setAnalytics] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const { profile, publications } = useDashboardExtras();

  useEffect(() => {
    Promise.all([api.get('/analytics/dashboard'), api.get('/videos')])
      .then(([a, v]) => { setAnalytics(a.data); setVideos(v.data); })
      .finally(() => setLoading(false));
  }, []);

  const hasContent = !loading && videos.length > 0;
  const hasActivity = videos.length > 0;
  const hasCalendarSignal = videos.some((v) => v.created_at);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="dashboard-page">

      {/* Empty state takes over for brand-new accounts */}
      {!loading && videos.length === 0 ? (
        <>
          <HeroV2 user={user} analytics={analytics} videos={videos} publications={publications} profile={profile} />
          <QuickActionsV2 videos={videos} />
          <EmptyOnboarding />
        </>
      ) : loading ? (
        <div className="space-y-6" data-testid="dashboard-loading">
          <SkeletonCard lines={3} />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} lines={1} />)}
          </div>
          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 space-y-3">
              {Array.from({ length: 3 }).map((_, i) => <SkeletonVideoRow key={i} />)}
            </div>
            <aside className="lg:col-span-4 space-y-3">
              <SkeletonCard lines={3} />
              <SkeletonCard lines={2} />
            </aside>
          </div>
        </div>
      ) : (
        <>
          {/* 1 · HERO — greeting + daily focus + personalized pill */}
          <HeroV2
            user={user}
            analytics={analytics}
            videos={videos}
            publications={publications}
            profile={profile}
          />

          {/* 2 · QUICK ACTIONS — 4 buttons only */}
          <QuickActionsV2 videos={videos} />

          {/* Main grid: Today + Featured on top, library + side panels below */}
          <div className="grid lg:grid-cols-12 gap-6 mb-8">
            <div className="lg:col-span-7 space-y-6">
              {/* 3 · TODAY FOCUS */}
              <TodayCard analytics={analytics} videos={videos} publications={publications} />

              {/* Library — real data */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-xl font-bold tracking-tight">{t('dash.library')}</h2>
                  <span className="text-xs text-zinc-500 font-mono">{t('dash.videos.count', { n: videos.length })}</span>
                </div>
                {hasContent && (
                  <div className="space-y-3">
                    {videos.slice(0, 6).map((v, i) => (
                      <motion.div key={v.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                        <Link
                          to={`/videos/${v.id}`}
                          className="group flex items-center gap-4 p-4 rounded-xl border border-white/5 bg-white/[0.02] hover:border-[#D4FF00]/40 hover:bg-white/[0.04] transition"
                          data-testid={`video-row-${v.id}`}
                        >
                          <div className="w-16 h-16 rounded-lg shrink-0 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${v.thumbnail_color}, #050505)` }}>
                            <div className="absolute inset-0 flex items-center justify-center"><Film className="w-5 h-5 text-white/80" /></div>
                            <div className="absolute bottom-1 right-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-black/70">{Math.round((v.duration || 0) / 60)}m</div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold truncate">{v.title}</h3>
                              {v.status === 'done' && <span className="text-[10px] px-1.5 py-0.5 rounded acid-chip uppercase font-mono">{t('dash.ready')}</span>}
                            </div>
                            <p className="text-xs text-zinc-500 mt-0.5 font-mono">{t('dash.row.subtitle', { type: v.source_type, n: v.clips_count, t: v.processing_time })}</p>
                          </div>
                          <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-[#D4FF00] group-hover:translate-x-0.5 transition" />
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Featured clip — real best clip */}
              {analytics?.best_clip && <FeaturedClip best={analytics.best_clip} />}

              {/* Content calendar — only rendered if there's at least one video with a created_at */}
              {hasCalendarSignal && <CalendarWidget videos={videos} />}
            </div>

            {/* Right rail */}
            <aside className="lg:col-span-5 space-y-6">
              {/* Credits status — quota visibility */}
              <CreditsCard />

              {/* 4 · AI INSIGHT — single reco */}
              <AIInsightCard analytics={analytics} profile={profile} />

              {/* 5 · CREATOR PROFILE (improved) */}
              <CreatorProfileCard />

              {/* Plan card — real data (usage/quota driven by plan) */}
              <PlanCard user={user} analytics={analytics} />

              {/* Activity — real events only, hides itself when empty */}
              {hasActivity && <ActivityTimeline videos={videos} />}

              {/* Referral */}
              <ReferralCard compact />
            </aside>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
